import React, {
    useCallback,
    useState
} from "react";
import {
    Button,
    OverlayTrigger,
    Tooltip
} from "react-bootstrap";
import PropTypes from "prop-types";

import UploadImageModal from "../../image/UploadImageModal";
import ImageModal from "../../image/ImageModal";
import UserVehicleEditModal from "./UserVehicleEditModal";
import UserVehicleRemoveModal from "./UserVehicleRemoveModal";
import UserVehicleDeleteImageModal from "./UserVehicleDeleteImageModal";
import Loading from "../../Loading";

function UserVehicle({ userVehicle, onVehicleUpdated, onVehicleRemoved, onImageDeleted, loading }) {
    const [showImageUploadModal, setShowImageUploadModal] = useState(false);

    const [showImageModal, setShowImageModal] = useState(false);
    const [imageModalImage, setImageModalImage] = useState(null);

    const [showEditModal, setShowEditModal] = useState(false);

    const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [loadingDeleteImage, setLoadingDeleteImage] = useState(false);
    const [error, setError] = useState(null);

    const onClickImage = useCallback((image) => {
        setShowImageModal(true);
        setImageModalImage(image);
    }, []);

    const deleteImage = useCallback(async () => {
        if (!onImageDeleted) {
            return;
        }
        setLoadingDeleteImage(true);
        setError(null);
        try {
            await onImageDeleted(userVehicle.id, imageModalImage?.id);
        } catch (requestError) {
            console.error(requestError);
            setError("Er is iets fout gegaan bij het verwijderen van de afbeelding. Probeer het later opnieuw.");
        } finally {
            setShowImageModal(false);
            setImageModalImage(null);
            setLoadingDeleteImage(false);
        }
    }, [userVehicle.id, imageModalImage?.id, onImageDeleted]);

    return (
        <div className="card mb-3">
            <UploadImageModal
                show={showImageUploadModal}
                handleClose={() => setShowImageUploadModal(false)}
                requestUrl="/uploadImageUserVehicle"
                requestParameters={{ userVehicleId: userVehicle.id }}
                imageUploaded={() => onVehicleUpdated?.()}
            />
            <ImageModal
                image={imageModalImage}
                show={showImageModal}
                handleClose={() => setShowImageModal(false)}
                handleDelete={() => setShowDeleteImageModal(true)}
            />
            <UserVehicleDeleteImageModal
                show={showDeleteImageModal}
                handleClose={() => setShowDeleteImageModal(false)}
                onDeleteImage={deleteImage}
            />
            <UserVehicleEditModal
                userVehicle={userVehicle}
                show={showEditModal}
                handleClose={() => setShowEditModal(false)}
                onVehicleUpdated={onVehicleUpdated}
            />
            <UserVehicleRemoveModal
                userVehicle={userVehicle}
                show={showRemoveModal}
                handleClose={() => setShowRemoveModal(false)}
                onVehicleRemoved={onVehicleRemoved}
            />
            <div className="card-body">
                <div>
                    {error && (
                        <div className="alert alert-danger">
                            {error}
                        </div>
                    )}
                    {loadingDeleteImage || loading && (
                        <Loading />
                    )}
                    {!loadingDeleteImage && !loading && (
                        <React.Fragment>
                            <div className="d-flex flex-row mb-1">
                                <h6 className="card-title flex-grow-1 mb-0">
                                    {userVehicle.manufacturer} {userVehicle.model}
                                    <span className="badge badge-secondary badge-pill ml-2">
                                        {userVehicle.buildYear}
                                    </span>
                                    <span className="badge badge-warning badge-pill ml-2">
                                        {userVehicle.licensePlate}
                                    </span>
                                    {userVehicle.horsePower && (
                                        <span className="badge badge-info badge-pill ml-2">
                                            {userVehicle.horsePower} pk
                                        </span>
                                    )}
                                </h6>
                                <div className="d-flex flex-row" style={{ gap: "0.5rem" }}>
                                    <OverlayTrigger overlay={(props) => (
                                        <Tooltip {...props}>
                                            Voertuig bewerken
                                        </Tooltip>
                                    )}>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => setShowEditModal(true)}
                                        >
                                            <i className="fa-solid fa-pen fa-fw" />
                                        </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger overlay={(props) => (
                                        <Tooltip {...props}>
                                            Afbeelding toevoegen
                                        </Tooltip>
                                    )}>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => setShowImageUploadModal(true)}
                                        >
                                            <i className="fa-solid fa-image fa-fw" />
                                        </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger overlay={(props) => (
                                        <Tooltip {...props}>
                                            Voertuig verwijderen
                                        </Tooltip>
                                    )}>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => setShowRemoveModal(true)}
                                        >
                                            <i className="fa-solid fa-trash fa-fw" />
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            </div>
                            {userVehicle.images.length > 0 && (
                                <div className="d-flex">
                                    {userVehicle.images.map((image) => (
                                        <div
                                            key={image.id}
                                            style={{ width: "25%" }}
                                        >
                                            <img
                                                src={image.url}
                                                className="card-img-top"
                                                alt={userVehicle.manufacturer + " " + userVehicle.model}
                                                style={{
                                                    width: "100%",
                                                    height: "auto",
                                                    objectFit: "cover",
                                                    aspectRatio: "1/1",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => onClickImage(image)} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </React.Fragment>
                    )}
                </div>
            </div>
        </div>
    );
}

UserVehicle.propTypes = {
    userVehicle: PropTypes.object.isRequired,
    onVehicleUpdated: PropTypes.func,
    onVehicleRemoved: PropTypes.func,
    onImageDeleted: PropTypes.func,
    loading: PropTypes.bool,
};

export default React.memo(UserVehicle);
